import type { FileListItem } from '@sparklayer-io/spark-dash-ui/components/ui/file-upload/types';
import { useStore } from '@tanstack/react-form';
import {
  Alert,
  Card,
  Checkbox,
  FileUpload,
  Icon,
  LearnMoreLink,
  Table,
  Text,
  TextLink,
} from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren } from 'react';
import { UnorderedList } from '../../../../components/unordered-list/unordered-list';
import { toggleUndefined } from '../../../../utils/form';
import { useDownloadCSVTemplate } from '../../hooks/use-download-csv-template';
import { usePriceListForm } from '../../hooks/use-price-list-form';

type ManualPricingProps = {
  priceListName?: string;
};

function ManualPricingCard({ children }: PropsWithChildren) {
  return (
    <Card>
      <Card.Header headingLevel="h2">{t('price_list_builder.manual_pricing.title')}</Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
}

export function ManualPricingForm(props: ManualPricingProps) {
  const form = usePriceListForm();

  const manualPricing = useStore(form.store, (state) => state.values.manualPricing);

  return (
    <ManualPricingCard>
      <Text size="md" variant="secondary">
        {t('price_list_builder.manual_pricing.enable.description')}
      </Text>
      <form.Field name="manualPricing">
        {(field) => (
          <Checkbox
            checked={!!field.state.value}
            onChange={() =>
              field.handleChange((prev) => {
                return toggleUndefined(prev, { file: undefined });
              })
            }
            error={field.state.meta.errors[0]?.message}
            label={t('price_list_builder.manual_pricing.enable.label')}
          />
        )}
      </form.Field>

      {manualPricing && (
        <form.Field name="manualPricing.file">
          {(field) => (
            <ManualPricing
              isBulk={false}
              selectedFile={field.state.value ?? undefined}
              onSelectedFileChanged={field.handleChange}
              csvErrors={field.state.meta.errors
                .map((e) => e?.message)
                .filter((e) => e !== undefined)}
              priceListName={props.priceListName}
            />
          )}
        </form.Field>
      )}
    </ManualPricingCard>
  );
}

export function ManualPricing(
  props: ManualPricingProps & {
    isBulk?: boolean;
    fileError?: string;
    csvErrors?: string[];
    selectedFile?: FileListItem | undefined;
    onSelectedFileChanged: (file: FileListItem | undefined) => void;
  },
) {
  const downloadCSVTemplate = useDownloadCSVTemplate();
  const isBulk = props.isBulk ?? !props.priceListName;

  return (
    <div className="flex flex-col gap-y-3">
      <Text size="md" variant="secondary">
        {t(`price_list_builder.manual_pricing.get_started.${isBulk ? 'bulk' : 'single'}`)}{' '}
        <LearnMoreLink link="https://docs.sparklayer.io/managing-pricing#QYPMk" />
      </Text>

      <div className="w-2/3">
        <Table
          headings={isBulk ? ['sku', 'price', 'price_list_slug'] : ['sku', 'price']}
          caption={t('price_list_builder.manual_pricing.csv_table_caption')}
        >
          <Table.Row id="example-row-1" index={1}>
            <Table.Cell>abc-01</Table.Cell>
            <Table.Cell>9.99</Table.Cell>
            {isBulk && <Table.Cell>base</Table.Cell>}
          </Table.Row>
          <Table.Row id="example-row-2" index={2}>
            <Table.Cell>abc-02</Table.Cell>
            <Table.Cell>19.99</Table.Cell>
            {isBulk && <Table.Cell>vip</Table.Cell>}
          </Table.Row>
        </Table>
      </div>

      <TextLink onClick={downloadCSVTemplate}>
        <span className="flex flex-row items-center gap-1">
          <Icon name="download" />
          {t('price_list_builder.manual_pricing.download_csv_template')}
        </span>
      </TextLink>

      <Text size="md" variant="secondary">
        {t('price_list_builder.manual_pricing.quantity_pricing')}{' '}
        <LearnMoreLink link="https://docs.sparklayer.io/quantity-pricing" />
      </Text>

      <FileUpload
        error={props.fileError}
        accept={['text/csv']}
        hintText={t('price_list_builder.manual_pricing.file_upload.hint')}
        onChange={(files) => props.onSelectedFileChanged(files?.[0])}
        value={props.selectedFile ? [props.selectedFile] : []}
      />

      {props.csvErrors && props.csvErrors.length > 0 && (
        <Alert
          headingLevel="h3"
          variant="error"
          title={t('price_list_builder.manual_pricing.errors.title')}
        >
          <div className="flex flex-col gap-2">
            <p>{t('price_list_builder.manual_pricing.errors.description')}</p>
            <UnorderedList>
              {props.csvErrors.map((error) => (
                <UnorderedList.Item key={error}> {error}</UnorderedList.Item>
              ))}
            </UnorderedList>
          </div>
        </Alert>
      )}
    </div>
  );
}
